(() => {
  if (customElements.get('rdb-greeting-card-options')) return;

  const transactions = new Map();
  const cardPropertyNames = new Set(['Greeting card', 'Card message', 'Grußkarte', 'Kartentext']);
  const validId = (value) => /^\d+$/.test(String(value)) && Number.isSafeInteger(Number(value)) && Number(value) > 0;
  const propertiesEqual = (actual, expected) => {
    const clean = (properties) => Object.entries(properties || {}).filter(([, value]) => value !== '' && value != null);
    const a = clean(actual);
    const b = clean(expected);
    return a.length === b.length && b.every(([key, value]) => String(actual?.[key]) === String(value));
  };

  class RdbGreetingCardOptions extends HTMLElement {
    static observedAttributes = ['data-product-id'];
    #controller;
    #observer;
    #expanded = false;
    #identity = '';
    #messageOpen = false;
    #messageAnimation;

    connectedCallback() {
      this.#controller?.abort();
      this.#observer?.disconnect();
      this.#controller = new AbortController();
      const { signal } = this.#controller;
      this.addEventListener('change', this.#onChange, { signal });
      this.addEventListener('input', this.#onInput, { signal });
      this.addEventListener('click', this.#onClick, { signal });
      document.addEventListener('rdb:currencychange', () => this.#refresh(), { signal });
      this.closest('form')?.addEventListener('reset', this.#onReset, { signal });
      this.#observer = new MutationObserver(this.#onChildrenChanged);
      this.#observer.observe(this, { childList: true, subtree: true });
      queueMicrotask(() => { if (this.isConnected) this.#refresh(); });
      customElements.whenDefined('product-form-component').then(() => {
        if (this.isConnected) this.#refresh();
      });
    }

    disconnectedCallback() {
      this.#controller?.abort();
      this.#observer?.disconnect();
      this.#messageAnimation?.cancel();
      this.#messageAnimation = null;
    }

    attributeChangedCallback() {
      queueMicrotask(() => { if (this.isConnected) this.#refresh(); });
    }

    #onChange = (event) => {
      if (event.target instanceof HTMLInputElement && event.target.matches('[data-card-design]')) this.#refresh();
    };

    #onInput = (event) => {
      if (event.target instanceof HTMLTextAreaElement && event.target.matches('[data-card-message]')) this.#updateCounter();
    };

    #onReset = (event) => {
      queueMicrotask(() => {
        if (!this.isConnected || event.defaultPrevented) return;
        this.#expanded = false;
        this.#setMessageOpen(false, false);
        this.#refresh();
      });
    };

    #onChildrenChanged = (records) => {
      const selector = '[data-card-design], [data-card-toggle], [data-card-message], .rdb-greeting-cards__grid';
      if (records.some((record) => [...record.addedNodes, ...record.removedNodes].some((node) =>
        node instanceof Element && (node.matches(selector) || node.querySelector(selector))
      )) && this.isConnected) this.#refresh();
    };

    #onClick = (event) => {
      if (!(event.target instanceof Element)) return;
      const messageToggle = event.target.closest('.rdb-message__toggle');
      if (messageToggle && this.contains(messageToggle)) {
        event.preventDefault();
        this.#setMessageOpen(!this.#messageOpen);
        return;
      }
      const toggle = event.target.closest('[data-card-toggle]');
      if (!toggle || !this.contains(toggle)) return;
      this.#expanded = !this.#expanded;
      this.#refresh();
      if (this.#expanded && event.detail === 0) this.querySelector('[data-card-extra] input:enabled')?.focus();
    };

    #setMessageOpen(open, animate = true) {
      const disclosure = this.querySelector('[data-message-disclosure]');
      const panel = disclosure?.querySelector('[data-message-panel]');
      if (!disclosure || !panel) return;
      const fromHeight = disclosure.open ? panel.getBoundingClientRect().height : 0;
      this.#messageAnimation?.cancel();
      this.#messageAnimation = null;
      this.#messageOpen = open;
      disclosure.dataset.expanded = String(open);
      if (!open && panel.contains(document.activeElement)) disclosure.querySelector('summary')?.focus();
      if (!animate || matchMedia('(prefers-reduced-motion: reduce)').matches || !panel.animate) {
        disclosure.open = open;
        return;
      }
      disclosure.open = true;
      const toHeight = open ? panel.scrollHeight : 0;
      const animation = panel.animate(
        [{ height: `${fromHeight}px` }, { height: `${toHeight}px` }],
        { duration: open ? 260 : 190, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
      );
      this.#messageAnimation = animation;
      animation.onfinish = () => {
        if (this.#messageAnimation !== animation) return;
        disclosure.open = open;
        this.#messageAnimation = null;
      };
    }

    #updateCounter() {
      const message = this.querySelector('[data-card-message]');
      const counter = this.querySelector('[data-card-counter]');
      if (!(message instanceof HTMLTextAreaElement)) return;
      if (message.value.length > 350) message.value = message.value.slice(0, 350);
      if (counter) counter.textContent = `${message.value.length} / 350`;
      const preview = this.querySelector('[data-message-preview]');
      if (preview) {
        const text = message.value.trim().replace(/\s+/g, ' ');
        if (preview.textContent !== text) preview.textContent = text;
        preview.hidden = !text;
      }
    }

    #refresh() {
      const identity = this.dataset.productId;
      if (identity !== this.#identity) {
        const changedProduct = Boolean(this.#identity);
        this.#identity = identity;
        this.#expanded = false;
        this.#setMessageOpen(false, false);
        if (changedProduct) {
          const free = this.querySelector('[data-card-design="message"]');
          if (free) free.checked = true;
          const message = this.querySelector('[data-card-message]');
          if (message) message.value = '';
          const anonymous = this.querySelector('[data-card-anonymous]');
          if (anonymous) anonymous.checked = false;
        }
      }
      const ready = this.closest('product-form-component')?.greetingCardAdapterReady === true;
      for (const input of this.querySelectorAll('[data-card-design]')) {
        if (input.dataset.cardDesign !== 'message') input.disabled = !ready || input.dataset.cardAvailable !== 'true' || !validId(input.dataset.cardVariantId);
      }
      let selected = this.querySelector('[data-card-design]:checked');
      if (!selected || selected.disabled) {
        selected = this.querySelector('[data-card-design="message"]');
        if (selected) selected.checked = true;
      }
      const extras = [...this.querySelectorAll('[data-card-extra]')];
      for (const option of extras) option.hidden = !this.#expanded && !option.contains(selected);
      const toggle = this.querySelector('[data-card-toggle]');
      if (toggle) {
        toggle.hidden = extras.length === 0;
        toggle.setAttribute('aria-expanded', String(this.#expanded));
        const label = toggle.querySelector('[data-card-toggle-label]');
        if (label) label.textContent = this.#expanded ? toggle.dataset.lessLabel : toggle.dataset.moreLabel;
      }
      const summary = this.querySelector('p[data-card-summary]');
      if (summary && selected) {
        const price = selected.dataset.cardPrice && window.rdbCurrencyPrices?.format(selected.dataset.cardPrice, selected.dataset.cardCurrency);
        const text = price && selected.dataset.cardSummaryTemplate
          ? selected.dataset.cardSummaryTemplate.replace('__RDB_DISPLAY_PRICE__', price)
          : selected.dataset.cardSummary;
        if (summary.textContent !== text) summary.textContent = text;
      }
      const fallback = this.querySelector('[data-card-fallback]');
      if (fallback) fallback.hidden = ready;
      this.#updateCounter();
      const disclosure = this.querySelector('[data-message-disclosure]');
      if (disclosure && !this.#messageAnimation) {
        disclosure.open = this.#messageOpen;
        disclosure.dataset.expanded = String(this.#messageOpen);
      }
    }

    static buildItems(formData, selection, group, cardProperty, messageProperty, quantityDefault = 1) {
      const id = formData.get('id');
      const quantity = Number(formData.get('quantity') || quantityDefault || 1);
      if (!validId(id) || !validId(selection.variantId) || !Number.isInteger(quantity) || quantity < 1) throw new Error('Invalid card request');
      const properties = {};
      const removed = new Set([...cardPropertyNames, cardProperty, messageProperty]);
      for (const [name, value] of formData.entries()) {
        const match = /^properties\[(.*)\]$/.exec(name);
        if (!match || removed.has(match[1])) continue;
        if (typeof value !== 'string') throw new Error('Unsupported property attachment');
        properties[match[1]] = value;
      }
      properties._rdb_card_group = group;
      properties._rdb_card_role = 'parent';
      const parent = { id: Number(id), quantity, properties };
      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) {
        if (!validId(sellingPlan)) throw new Error('Invalid selling plan');
        parent.selling_plan = Number(sellingPlan);
      }
      const childProperties = { _rdb_card_group: group, _rdb_card_role: 'card' };
      const message = String(selection.message || '').slice(0, 350);
      if (message) childProperties[messageProperty] = message;
      return [parent, { id: Number(selection.variantId), quantity: 1, parent_id: Number(id), properties: childProperties }];
    }

    static inspectItems(actualItems, expectedItems) {
      if (!Array.isArray(actualItems)) return 'unknown';
      const group = expectedItems[0].properties._rdb_card_group;
      const lines = actualItems.filter((item) => item.properties?._rdb_card_group === group);
      if (lines.length === 0) return 'absent';
      if (lines.length !== 2) return 'partial';
      const parent = lines.find((item) => item.properties?._rdb_card_role === 'parent');
      const child = lines.find((item) => item.properties?._rdb_card_role === 'card');
      if (!parent || !child || !parent.key || child.parent_relationship?.parent_key !== parent.key) return 'partial';
      for (const [index, actual] of [parent, child].entries()) {
        const expected = expectedItems[index];
        if (Number(actual.variant_id || actual.id) !== expected.id || Number(actual.quantity) !== expected.quantity || !propertiesEqual(actual.properties, expected.properties)) return 'partial';
        const plan = actual.selling_plan_allocation?.selling_plan?.id;
        if (String(plan || '') !== String(expected.selling_plan || '')) return 'partial';
      }
      return 'complete';
    }

    get hasPendingCartRequest() {
      return transactions.has(`${location.pathname}:${this.closest('form')?.id}:${this.dataset.productId}`);
    }

    submitCartRequest({ formData, url, sectionIds, quantityDefault }) {
      const form = this.closest('form');
      const key = `${location.pathname}:${form?.id}:${this.dataset.productId}`;
      const previous = transactions.get(key);
      if (previous?.inFlight) return previous.inFlight;
      const input = this.querySelector('[data-card-design]:checked');
      if (!previous && input?.dataset.cardDesign === 'message') return null;
      const selection = input && !input.disabled && input.dataset.cardAvailable === 'true' && validId(input.dataset.cardVariantId)
        ? { variantId: input.dataset.cardVariantId, message: this.querySelector('[data-card-message]')?.value || '' }
        : null;
      const sections = [...new Set(sectionIds)].slice(0, 5);
      const sectionsUrl = location.pathname + location.search;
      const review = this.querySelector('[data-card-review]');
      const error = (needsReview, original) => {
        if (review) review.hidden = !needsReview;
        const message = needsReview ? this.dataset.reviewError : original?.description || this.dataset.requestError;
        return { status: original?.status || 422, message, description: message, rdbAddedItemCount: 0 };
      };
      const readCart = async () => {
        const result = await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!result.ok) throw new Error('Cart readback failed');
        return result.json();
      };
      const success = async (transaction, original) => {
        let renderedSections = original?.sections;
        if (!renderedSections && sections.length) {
          try {
            const target = new URL(location.href);
            target.searchParams.delete('section_id');
            target.searchParams.set('sections', sections.join(','));
            const result = await fetch(target.pathname + target.search, { cache: 'no-store' });
            if (result.ok) renderedSections = await result.json();
          } catch {}
        }
        transactions.delete(key);
        if (review) review.hidden = true;
        return { items: original?.items || [], sections: renderedSections, rdbAddedItemCount: transaction.items.reduce((sum, item) => sum + item.quantity, 0) };
      };
      let pendingTask;
      const run = async () => {
        let group = previous?.items[0].properties._rdb_card_group;
        if (previous) {
          try {
            const cart = await readCart();
            const state = RdbGreetingCardOptions.inspectItems(cart.items, previous.items);
            if (state === 'complete') return success(previous);
            if (state !== 'absent') return error(true);
          } catch { return error(true); }
          if (!selection) {
            if (input?.dataset.cardDesign !== 'message') {
              transactions.delete(key);
              return error(false);
            }
            formData.set('sections', sections.join(','));
            formData.set('sections_url', sectionsUrl);
            try {
              const result = await fetch(url, { method: 'POST', headers: { Accept: 'application/json' }, body: formData });
              return await result.json();
            } finally { transactions.delete(key); }
          }
        }
        if (!selection) return error(false);
        if (!group) group = crypto.randomUUID();
        let transaction;
        try {
          transaction = { items: RdbGreetingCardOptions.buildItems(formData, selection, group, this.dataset.cardProperty, this.dataset.messageProperty, quantityDefault), inFlight: pendingTask };
        } catch { return error(false); }
        transactions.set(key, transaction);
        let response;
        try {
          const result = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: transaction.items, sections, sections_url: sectionsUrl }),
          });
          response = await result.json();
          if (result.ok && !response.status && RdbGreetingCardOptions.inspectItems(response.items, transaction.items) === 'complete') return success(transaction, response);
        } catch {}
        try {
          const cart = await readCart();
          const state = RdbGreetingCardOptions.inspectItems(cart.items, transaction.items);
          if (state === 'complete') return success(transaction, response);
          if (state === 'absent') return error(false, response);
          return error(true);
        } catch { return error(true); }
      };
      pendingTask = run();
      const active = transactions.get(key);
      if (active) {
        active.inFlight = pendingTask;
        pendingTask.finally(() => {
          active.inFlight = null;
          const current = transactions.get(key);
          if (current?.inFlight === pendingTask) current.inFlight = null;
        }).catch(() => {});
      }
      return pendingTask;
    }
  }

  customElements.define('rdb-greeting-card-options', RdbGreetingCardOptions);
})();
